import { useMemo } from 'react';

import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import type { MaintenanceListTableDisplay } from '@/components/sections/service/maintenance-list/table';
import { Button } from '@/components/shared/buttons/button';
import { Select } from '@/components/shared/form/select';
import { HeadingItem } from '@/components/shared/headline/heading-item';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { generateCommonImageSources } from '@/components/shared/media/image/generate-sources';
import { config } from '@/stitches.config';
import { IVehicleType } from '@/types/services/contentstack';
import {
  ContentstackAllVehicleTypeMaintenanceOptionResponse,
  RegularMaintenaceScheduleOnlyMonthMileage,
} from '@/types/services/contentstack/services/maintenance-service';
import { IButton } from '@/types/shared';

export type MaintenanceListSearchFilterOptionItem =
  ContentstackAllVehicleTypeMaintenanceOptionResponse &
    RegularMaintenaceScheduleOnlyMonthMileage;
export type MaintenanceListSearchFilterOption = {
  label: string;
  value: string;
  item?: MaintenanceListSearchFilterOptionItem;
  children?: any[];
};
export type MaintenanceListSearchFilterSelectOptionData = {
  key: string;
  placeholder: string;
  options: MaintenanceListSearchFilterOption[];
};

export interface MaintenanceListSearchFilterData {
  options: MaintenanceListSearchFilterSelectOptionData[];
  selectOptionValues: string[];
  setSelectOption: (index: number, value: string) => void;
  vehicleType?: IVehicleType;
}

export interface MaintenanceListSearchFilterCtaData {
  title: string;
  description?: string;
  headingTitleLead?: string;
  underHeadingButtons?: IButton[];
  backgroundImageMobile?: string;
  backgroundImageDesktop?: string;
  hasSearchButton?: boolean;
  buttonSearchTrackUrl?: string;
  onClickSearchButton?: () => void;
}

export interface MaintenanceListSearchFilterProps
  extends MaintenanceListSearchFilterData,
    MaintenanceListSearchFilterCtaData {
  tableDisplay?: MaintenanceListTableDisplay;
  isCta?: boolean;
}

export const MaintenanceListSearchFilter = ({
  headingTitleLead,
  title,
  description,
  options,
  selectOptionValues,
  setSelectOption,
  backgroundImageDesktop,
  backgroundImageMobile = '',
  isCta,
  underHeadingButtons,
  buttonSearchTrackUrl,
  hasSearchButton = true,
  onClickSearchButton,
  vehicleType,
}: MaintenanceListSearchFilterProps) => {
  const isDesktop = useMediaQuery(config.media.xl);
  const dataTestSection = useMemo(
    () =>
      isCta
        ? 'section_maintananceListCta'
        : 'section_maintenanceList_header_filter',
    [isCta]
  );
  const isSelectedAllOptions = useMemo(() => {
    const optionsToCheck =
      isDesktop && vehicleType === 'cv'
        ? selectOptionValues.slice(0, 3)
        : selectOptionValues;
    return optionsToCheck.every((value) => value !== '');
  }, [selectOptionValues, vehicleType, isDesktop]);
  const onOptionChange = (
    index: number,
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectOption(index, e.target.value);
  };
  return (
    <Section
      size={isCta ? '2xl' : undefined}
      backgroundColor={isCta ? 'gray-dark' : 'white'}
      test={dataTestSection}
      css={isCta ? { '@lg': { minHeight: 500 } } : undefined}
    >
      {!!isCta && (
        <Box
          position="absolute"
          inset="0"
          pointerEvents="none"
          css={{
            opacity: '0.2',
          }}
        >
          <Image
            rootMargin="20% 0px 0px 0px"
            src={backgroundImageMobile ?? ''}
            alt={title}
            width={1600}
            height={900}
            layout="fill"
            sources={generateCommonImageSources({
              mobileSrc: backgroundImageMobile,
              mobileOptions: {
                quality: 65,
              },
              desktopSrc: backgroundImageDesktop,
              desktopOptions: {
                quality: 65,
              },
            })}
          />
        </Box>
      )}
      <Container>
        <Box paddingBottom="10">
          <Grid
            alignItems={{ '@lg': 'start' }}
            templateColumns={isCta ? { '@lg': '12' } : '1'}
            columnGap={isCta ? { '@lg': '8', '@xl': '2' } : undefined}
            rowGap={isCta ? '10' : undefined}
          >
            <Section
              as="div"
              size={isCta ? undefined : 'sm'}
              test={`${dataTestSection}_headingItem`}
              css={
                isCta
                  ? {
                      '@lg': {
                        gridColumn: '1 / span 6',
                      },
                      '@xl': {
                        gridColumn: '1 / span 4',
                      },
                    }
                  : undefined
              }
            >
              <HeadingItem
                lead={headingTitleLead}
                title={title}
                description={description}
                isLine={!isCta}
                isDarkTheme={isCta}
                buttons={isCta ? underHeadingButtons : undefined}
              />
            </Section>
            <Box
              css={
                isCta
                  ? {
                      '@lg': {
                        gridColumn: '7 / span 6',
                      },
                      '@xl': {
                        gridColumn: '8 / span 5',
                      },
                    }
                  : undefined
              }
            >
              <Grid
                columnGap="4"
                css={
                  isCta
                    ? {
                        rowGap: '$space-4',
                        gridTemplateColumns: '1fr',
                      }
                    : {
                        rowGap: '$space-2',
                        gridTemplateColumns: '1fr',
                        gridTemplateRows: '$space-1',
                        '@sm': {
                          rowGap: '$space-4',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gridTemplateRows: '$space-2',
                        },
                        // Adjust init grid columns to prevent CLS
                        '@lg': {
                          gridTemplateColumns:
                            vehicleType === 'lcv'
                              ? 'repeat(3, 1fr)'
                              : 'repeat(4, 1fr)',
                          gridTemplateRows: '$space-1',
                        },
                      }
                }
              >
                {options.map((option, index) => (
                  <Box
                    key={`selectOption${option.key}${index}`}
                    css={
                      index === 0
                        ? {
                            '@sm': {
                              gridColumn: `1 / span ${
                                vehicleType === 'cv' ? 1 : 2
                              }`,
                            },
                            '@lg': { gridColumn: '1 / span 1' },
                          }
                        : undefined
                    }
                  >
                    <Select
                      name={option.key}
                      onChange={(e) => onOptionChange(index, e)}
                      value={selectOptionValues[index]}
                      disabled={
                        index > 0 && selectOptionValues[index - 1]?.length === 0
                      }
                    >
                      <option value="">{option.placeholder}</option>
                      {option.options.map((item, index) => (
                        <option
                          key={`${option.key}Option${index}`}
                          value={item.value}
                        >
                          {item.label}
                        </option>
                      ))}
                    </Select>
                  </Box>
                ))}
                {!!isCta && !!hasSearchButton && (
                  <Box
                    css={{
                      textAlign: 'center',
                      '@sm': {
                        textAlign: 'left',
                      },
                    }}
                  >
                    <Button
                      label="ค้นหา"
                      onClick={onClickSearchButton}
                      disabled={!isSelectedAllOptions}
                      tracking={{
                        dataTrack: 'service-maintenance-list-cta',
                        dataTrackText: 'ค้นหา',
                        dataTrackUrl:
                          buttonSearchTrackUrl ?? '/service/maintenance-list',
                      }}
                      test={{
                        dataTest: 'maintenanceListCta_button_search',
                      }}
                    />
                  </Box>
                )}
              </Grid>
            </Box>
          </Grid>
        </Box>
      </Container>
    </Section>
  );
};
MaintenanceListSearchFilter.displayName =
  'SectionServiceMaintenanceListSearchFilter';
